import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { fetchRun } from '../api.js'
import { RunState } from './status.jsx'
import type { RunSummary } from '../api.js'

const TERMINAL = new Set(['succeeded', 'failed', 'rejected', 'expired'])

/**
 * States nothing here can move, so polling them is pointless.
 *
 * A run awaiting approval (IM-102, IM-39) waits on a person, for up to a week:
 * three-second polls for seven days would be two hundred thousand reads of a
 * value that changes when somebody clicks a button on another screen. So it is
 * treated like a terminal state here, and the line says where to look instead.
 */
const STILL = new Set([...TERMINAL, 'awaiting-approval'])

const WAITING_FOR: Readonly<Record<string, string>> = {
  customer: 'the customer’s approval',
  'second-approver': 'a second person to approve it',
}

/**
 * Follows a run that was just started, until it stops.
 *
 * Polling *this* console's own API, which is a different thing from polling a
 * customer's cloud: the run record is updated by the completion event, so this
 * reads a value that is already there rather than reaching into someone else's
 * account. It stops as soon as the run is terminal, and on unmount.
 *
 * Three seconds because a plan finishes in a minute or two and an apply in an
 * hour; nobody is watching a progress line for an hour, and the deployment
 * screen shows the same state on reload.
 */
export const RunProgress = ({
  baseUrl,
  tenantId,
  run,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly run: RunSummary
}): JSX.Element => {
  const [latest, setLatest] = useState(run)

  useEffect(() => {
    setLatest(run)
    if (STILL.has(run.state)) return

    let live = true
    const timer = setInterval(() => {
      void fetchRun(baseUrl, tenantId, run.runId)
        .then((answer) => {
          if (!live) return
          setLatest(answer)
          if (STILL.has(answer.state)) clearInterval(timer)
        })
        // A failed poll is not a failed run. The record is authoritative and
        // the next tick will read it again.
        .catch(() => undefined)
    }, 3000)

    return () => {
      live = false
      clearInterval(timer)
    }
  }, [baseUrl, tenantId, run])

  return (
    <p className="run-progress" role="status">
      <code>{latest.runId}</code> {latest.command} — <RunState state={latest.state} />
      {latest.failure === undefined ? null : (
        <>
          {' — '}
          {latest.failure.detail}
        </>
      )}
      {latest.state === 'awaiting-approval' ? (
        <>
          {' — '}
          nothing has been started. It waits for{' '}
          {WAITING_FOR[latest.approval?.requirement ?? ''] ?? 'approval'}
          {latest.approval?.expiresAt === undefined ? '' : ` until ${latest.approval.expiresAt}`},
          and runs as soon as that is given.
        </>
      ) : null}
    </p>
  )
}
